
import { useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { useQuery } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { supabase } from "@/integrations/supabase/client"
import type { Database } from "@/integrations/supabase/types"

type MonthlyTierPrompt = Database["public"]["Tables"]["monthly_tier_prompts"]["Row"]
type SubscriptionTier = Database["public"]["Enums"]["subscription_tier"]

export default function MonthlyTierPromptsSection() {
  const { toast } = useToast()
  const [selectedMonth, setSelectedMonth] = useState<string>(
    (new Date().getMonth() + 1).toString()
  )
  const [selectedYear, setSelectedYear] = useState<string>(
    new Date().getFullYear().toString()
  )
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>("essentials")

  const { data: monthlyTierPrompts, refetch } = useQuery({
    queryKey: ["monthly_tier_prompts", selectedMonth, selectedYear],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("monthly_tier_prompts")
        .select("*")
        .eq("month", parseInt(selectedMonth))
        .eq("year", parseInt(selectedYear))
        .order("subscription_tier")

      if (error) {
        toast({
          variant: "destructive",
          title: "Error loading monthly tier prompts",
          description: error.message,
        })
        return []
      }

      return data as MonthlyTierPrompt[]
    },
  })

  const handleSavePrompt = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const backendPrompt = formData.get("backend_prompt") as string
    const userSummary = formData.get("user_summary") as string
    const active = formData.get("active") === "true"

    const { error } = await supabase.from("monthly_tier_prompts").upsert({
      subscription_tier: selectedTier,
      month: parseInt(selectedMonth),
      year: parseInt(selectedYear),
      backend_prompt: backendPrompt,
      user_summary: userSummary,
      active,
    })

    if (error) {
      toast({
        variant: "destructive",
        title: "Error saving prompt",
        description: error.message,
      })
    } else {
      toast({
        title: "Monthly tier prompt saved successfully",
      })
      refetch()
    }
  }

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ]

  const currentYear = new Date().getFullYear()
  const years = Array.from(
    { length: 3 },
    (_, i) => (currentYear - 1 + i).toString()
  )

  const tiers: SubscriptionTier[] = ["essentials", "pro", "elite"]
  const currentTierPrompt = monthlyTierPrompts?.find(
    p => p.subscription_tier === selectedTier
  )

  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select month" />
          </SelectTrigger>
          <SelectContent>
            {months.map((month, index) => (
              <SelectItem key={index + 1} value={(index + 1).toString()}>
                {month}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent>
            {years.map((year) => (
              <SelectItem key={year} value={year}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedTier} onValueChange={(value) => setSelectedTier(value as SubscriptionTier)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select tier" />
          </SelectTrigger>
          <SelectContent>
            {tiers.map((tier) => (
              <SelectItem key={tier} value={tier}>
                {tier.charAt(0).toUpperCase() + tier.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Monthly {selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} Tier Prompt</CardTitle>
          <CardDescription>
            Manage the backend prompt and user-facing summary for {months[parseInt(selectedMonth) - 1]} {selectedYear}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSavePrompt} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="backend_prompt">Backend Prompt Template</Label>
              <Textarea
                id="backend_prompt"
                name="backend_prompt"
                rows={10}
                required
                defaultValue={currentTierPrompt?.backend_prompt}
                placeholder="Enter the backend prompt template for AI processing..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user_summary">User-Facing Summary</Label>
              <Textarea
                id="user_summary"
                name="user_summary"
                rows={5}
                required
                defaultValue={currentTierPrompt?.user_summary}
                placeholder="Enter the summary that will be shown to users..."
              />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <RadioGroup
                name="active"
                defaultValue={currentTierPrompt?.active?.toString() ?? "true"}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="active" />
                  <Label htmlFor="active">Active</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="inactive" />
                  <Label htmlFor="inactive">Inactive</Label>
                </div>
              </RadioGroup>
            </div>

            <Button type="submit" className="w-full">
              Save {selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} Tier Prompt
            </Button>
          </form>
        </CardContent>
      </Card>

      {monthlyTierPrompts && monthlyTierPrompts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Other Tiers for {months[parseInt(selectedMonth) - 1]} {selectedYear}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {monthlyTierPrompts
                .filter(prompt => prompt.subscription_tier !== selectedTier)
                .map(prompt => (
                  <div key={prompt.id} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold">
                        {prompt.subscription_tier.charAt(0).toUpperCase() + prompt.subscription_tier.slice(1)}
                      </h4>
                      <span className={`px-2 py-1 rounded text-sm ${
                        prompt.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {prompt.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{prompt.user_summary}</p>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
